import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Loader2, Mail, CheckCircle2 } from "lucide-react";

const Broadcast = () => {
  const [subject, setSubject] = useState("🔥 Trending on AfriMarket + meet Plyx & our Makers platform");
  const [intro, setIntro] = useState("");
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(null);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/broadcast", { subject, intro, test: true });
      toast.success(`Test email sent to ${data.sent_to} — check your inbox`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const send = async () => {
    if (!window.confirm("Send this email to ALL opted-in registered users now? This cannot be undone.")) return;
    setSending(true);
    setDone(null);
    setProgress(null);
    try {
      const { data } = await api.post("/admin/broadcast", { subject, intro });
      toast(`Sending to ${data.recipients} users in the background…`);
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const jr = await api.get(`/admin/broadcast-job/${jobId}`);
          const j = jr.data;
          setProgress({ processed: j.processed, total: j.total, sent: j.sent, failed: j.failed });
          if (j.status === "done") {
            setDone({ sent: j.sent, failed: j.failed });
            toast.success(`Broadcast sent to ${j.sent} users${j.failed ? `, ${j.failed} failed` : ""}`);
            setSending(false);
            return;
          }
        } catch (e) { /* keep polling */ }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-broadcast">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-[#C05A3C]" />
        <h3 className="font-display text-2xl">Email broadcast</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Sends a branded email to <strong>all opted-in registered users</strong>. It automatically
        includes your current <strong>trending products</strong> (best-sellers), a <strong>Plyx.co.uk</strong> invite,
        and your <strong>Makers &amp; Innovation</strong> platform — plus a legally-required unsubscribe link.
      </p>

      <div>
        <label className="text-sm font-medium">Subject line</label>
        <Input
          data-testid="broadcast-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <label className="text-sm font-medium">Personal intro message <span className="text-muted-foreground">(optional)</span></label>
        <Textarea
          data-testid="broadcast-intro"
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          rows={3}
          placeholder="e.g. We've just added fresh Ankara arrivals this week — don't miss out!"
          className="mt-1"
        />
      </div>

      <div className="rounded-lg bg-[#FBF6EE] border border-black/5 p-4 text-sm text-muted-foreground flex gap-2">
        <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#D9923B]" />
        <span>Auto-included: 6 trending products with images &amp; prices · “Register on Plyx.co.uk” · “Explore the Makers platform” · unsubscribe footer.</span>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Button
          onClick={send}
          disabled={sending}
          data-testid="broadcast-send-btn"
          className="bg-[#C05A3C] text-white hover:bg-[#A84B30]"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
          {sending && progress ? `Sending ${progress.processed}/${progress.total}…` : "Send to all users"}
        </Button>
        <Button
          variant="outline"
          onClick={sendTest}
          disabled={sending || testing}
          data-testid="broadcast-test-btn"
        >
          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Send test to me
        </Button>
      </div>

      {done && (
        <div className="flex items-center gap-2 text-sm text-[#4A5D23]" data-testid="broadcast-done">
          <CheckCircle2 className="w-4 h-4" /> Sent to {done.sent} users{done.failed ? ` · ${done.failed} failed` : ""}.
        </div>
      )}
    </div>
  );
};

export default Broadcast;
